(() => {
  'use strict';

  const RUNTIME_KEY = '__gdcHalloweenGift888Runtime';
  if (window[RUNTIME_KEY]?.initialized) return;
  const state = { initialized: true, syncing: false, timer: null, cartUpdateUnsubscriber: null };
  window[RUNTIME_KEY] = state;

  const MARKER_KEY = '_gdc_halloween_gift';
  const MARKER_VALUE = 'pumpkin_888';
  const config = () => document.getElementById('GdcHalloweenGiftConfig');
  const enabled = () => config()?.dataset?.enabled === 'true';
  const threshold = () => Number(config()?.dataset?.threshold || 88800);
  const giftVariantId = () => Number(config()?.dataset?.variantId || 0);
  const isZh = () => String(document.documentElement.lang || '').toLowerCase().startsWith('zh');
  const root = () => window.Shopify?.routes?.root || '/';
  const endpoint = (path) => (root() + path).replace(/\\+/g, '/').replace('https:/', 'https://');
  const money = (cents) => 'HK$' + Math.ceil(Math.max(0, cents) / 100);

  const isGiftVariant = (item) => Number(item?.variant_id) === giftVariantId();
  const isManagedGift = (item) => isGiftVariant(item);
  // Gift S is HK$0, so Shopify cart.total_price is the authoritative
  // post-discount merchandise total for the HK$888 threshold.
  const eligibleSubtotal = (cart) => Math.max(0, Number(cart?.total_price || 0));

  const fetchCart = async () => {
    const response = await fetch(endpoint('cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart read failed: ' + response.status);
    return response.json();
  };

  const mutateCart = async (path, body) => {
    const response = await fetch(endpoint(path), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign({}, body, {
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      }))
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.description || payload?.message || ('Cart update failed: ' + response.status));
    return payload;
  };

  const renderDrawer = (payload) => {
    if (!payload?.sections) return;
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(payload);
  };

  const statusParents = () => {
    const parents = [];
    const drawerClarity = document.querySelector('cart-drawer [data-gdc-cart-clarity], cart-drawer .gdc-cart-clarity');
    if (drawerClarity?.parentElement) parents.push(drawerClarity.parentElement);
    const pageClarity = document.querySelector('#main-cart-footer .gdc-cart-clarity');
    if (pageClarity?.parentElement && !parents.includes(pageClarity.parentElement)) parents.push(pageClarity.parentElement);
    return parents;
  };

  const renderStatus = (cart, overrideMessage) => {
    const subtotal = eligibleSubtotal(cart);
    const gift = (cart?.items || []).find(isManagedGift);
    let message = overrideMessage || '';
    if (!message) {
      if (subtotal >= threshold()) {
        message = gift
          ? (isZh() ? '🎃 折扣後滿 HK$888，南瓜袋已免費加入購物車。' : '🎃 Your free Pumpkin Bag has been added after reaching HK$888 after discounts.')
          : (isZh() ? '🎃 折扣後已達 HK$888，正在加入免費南瓜袋…' : '🎃 HK$888 after discounts reached — adding your free Pumpkin Bag…');
      } else {
        const remaining = threshold() - subtotal;
        message = isZh()
          ? ('🎃 折扣後再購 ' + money(remaining) + ' 即送南瓜袋。')
          : ('🎃 Spend ' + money(remaining) + ' more after discounts to receive a free Pumpkin Bag.');
      }
    }

    statusParents().forEach((parent) => {
      let box = parent.querySelector('[data-gdc-halloween-gift-status]');
      if (!box) {
        box = document.createElement('div');
        box.dataset.gdcHalloweenGiftStatus = '';
        box.setAttribute('role', 'status');
        box.setAttribute('aria-live', 'polite');
        box.style.cssText = 'margin:.65rem 0 .85rem;padding:.65rem .8rem;border:1px solid rgba(var(--color-foreground),.14);border-radius:6px;background:rgba(var(--color-foreground),.035);font-size:1.15rem;line-height:1.4;';
        const clarity = parent.querySelector('[data-gdc-cart-clarity], .gdc-cart-clarity');
        if (clarity) clarity.before(box); else parent.prepend(box);
      }
      if (box.textContent !== message) box.textContent = message;
    });
  };

  const onCartPage = () => {
    const cartPath = (root() + 'cart').replace(/\\+$/, '');
    return window.location.pathname.replace(/\\+$/, '') === cartPath;
  };

  const afterMutation = async (payload) => {
    document.dispatchEvent(new CustomEvent('gdc:cart-state-changed'));
    if (onCartPage()) {
      window.location.reload();
      return;
    }
    renderDrawer(payload);
    window.setTimeout(async () => {
      try { renderStatus(await fetchCart()); } catch (_) {}
    }, 180);
  };

  const addGift = () => mutateCart('cart/add.js', {
    items: [{ id: giftVariantId(), quantity: 1, properties: { [MARKER_KEY]: MARKER_VALUE } }]
  });
  const changeGift = (key, quantity) => mutateCart('cart/change.js', { id: key, quantity });

  const sync = async () => {
    if (!enabled() || !giftVariantId() || state.syncing) return;
    state.syncing = true;
    try {
      const cart = await fetchCart();
      const subtotal = eligibleSubtotal(cart);
      const giftLines = (cart.items || []).filter(isManagedGift);
      const gift = giftLines[0];

      if (subtotal >= threshold()) {
        if (!gift) {
          renderStatus(cart);
          await afterMutation(await addGift());
          return;
        }
        if (gift.quantity !== 1) {
          await afterMutation(await changeGift(gift.key, 1));
          return;
        }
        if (giftLines.length > 1) {
          let lastPayload = null;
          for (let i = 1; i < giftLines.length; i += 1) {
            lastPayload = await changeGift(giftLines[i].key, 0);
          }
          if (lastPayload) await afterMutation(lastPayload);
          return;
        }
      } else if (giftLines.length) {
        let lastPayload = null;
        for (const line of giftLines) {
          lastPayload = await changeGift(line.key, 0);
        }
        if (lastPayload) await afterMutation(lastPayload);
        return;
      }

      renderStatus(cart);
    } catch (error) {
      try {
        const cart = await fetchCart();
        renderStatus(cart, isZh()
          ? '🎃 南瓜袋暫時未能自動加入，請稍後再試。'
          : '🎃 The Pumpkin Bag could not be added automatically. Please try again.');
      } catch (_) {}
      console.warn('[GDC Halloween Gift]', error);
    } finally {
      state.syncing = false;
    }
  };

  const schedule = (delay = 120) => {
    window.clearTimeout(state.timer);
    state.timer = window.setTimeout(sync, delay);
  };

  const installCartMutationWatcher = () => {
    if (window.__gdcHalloweenGiftFetchWatcherBound) return;
    window.__gdcHalloweenGiftFetchWatcherBound = true;

    const nativeFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const input = args[0];
      const url = typeof input === 'string' ? input : (input?.url || '');
      const response = await nativeFetch(...args);

      if (
        !state.syncing &&
        /\/cart\/(add|change|update)(\.js)?(?:\?|$)/.test(url)
      ) {
        schedule(60);
      }
      return response;
    };
  };

  const boot = () => {
    installCartMutationWatcher();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try { state.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, schedule); } catch (_) {}
    }
    schedule();
    window.addEventListener('pageshow', schedule);
    document.addEventListener('gdc:cart-state-changed', schedule);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();